import { useUser } from '@clerk/clerk-react';
import React from 'react';
import { 
  User, Sparkles, BrainCircuit, Globe, Mail, Settings, 
  CheckCircle2, ShoppingCart, Shirt, Cpu, Car, 
  Home, Heart, Activity, Palette, AlertCircle, Info, List
} from 'lucide-react';
import { UserProfile } from '../../../../types';
import { liveState, useStore } from '../../../lib/store';
import { useTranslation } from 'react-i18next';

interface UserProfileCardProps {
  profile: UserProfile;
}

export const UserProfileCard: React.FC<{ props: UserProfileCardProps; isStreaming?: boolean }> = ({ 
  props,
  isStreaming
}) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const [failedAvatar, setFailedAvatar] = React.useState<string | null>(null);
  const globalStoreProfile = useStore(state => state.userProfile);
  
  // AI might pass { profile: { ... } } or just { ... } as props
  const aiProfileData = (props as any)?.profile || props;
  
  // The live profile wins over what the model passed: the card always shows the
  // current profile, and the profile tool's summary isn't shaped like UserProfile.
  let profile = aiProfileData;
  if (liveState.profile) {
    profile = { ...profile, ...liveState.profile };
  }
  if (globalStoreProfile) {
    profile = { ...profile, ...globalStoreProfile };
  }

  // Match the Account page's live Clerk photo; never let an old generated card
  // override it with a missing, placeholder, or stale image URL.
  const avatarUrl = user?.imageUrl || globalStoreProfile?.avatar_url || liveState.profile?.avatar_url;

  // Final validation check after recovery
  if ((!profile || !profile.email) && !isStreaming) {
    return (
      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm text-center">
        <p className="text-xs font-black text-gray-400 uppercase tracking-widest">{t('chat.incomplete_data')}</p>
      </div>
    );
  }

  const aiProfile = profile?.ai_profile;
  const domains = aiProfile?.domains || {};
  
  const SectionHeader = ({ icon: Icon, title, count }: { icon: any, title: string, count?: number }) => (
    <div className="flex justify-between items-center px-1 mb-3">
      <div className="flex items-center gap-2">
        <div className="p-1.5 bg-emerald-50 text-emerald-600 rounded-lg">
          <Icon size={12} />
        </div>
        <h5 className="text-[10px] font-black uppercase text-[#1a2b3c] tracking-widest">{title}</h5>
      </div>
      {count !== undefined && (
        <span className="text-[8px] font-black text-emerald-500 uppercase">{t('settings.fields.configured_count', { count })}</span>
      )}
    </div>
  );

  const DataPoint = ({ label, value }: { label: string, value: any }) => {
    if (value === undefined || value === null || (Array.isArray(value) && value.length === 0)) return null;
    const displayValue = Array.isArray(value) ? value.join(', ') : value.toString();
    
    return (
      <div>
        <p className="text-[8px] font-bold text-gray-400 uppercase mb-0.5">{label}</p>
        <p className="text-[10px] font-bold text-[#1a2b3c] leading-tight">{displayValue}</p>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-xl overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-300">
      {/* Header */}
      <div className="p-6 bg-[#1a2b3c] text-white">
        <div className="flex items-center gap-4 mb-4">
          <div className="relative">
            {avatarUrl && avatarUrl !== failedAvatar ? (
              <img src={avatarUrl} alt={t('account.tab_profile', { defaultValue: 'Profile' })} onError={() => setFailedAvatar(avatarUrl)} className="w-16 h-16 rounded-2xl object-cover border-2 border-emerald-500 shadow-lg" />
            ) : (
              <div className="w-16 h-16 rounded-2xl bg-[#2a3b4c] border-2 border-emerald-500/30 flex items-center justify-center text-emerald-400 shadow-lg relative overflow-hidden">
                <div className="absolute inset-0 bg-emerald-500/10 animate-pulse" />
                <User size={32} strokeWidth={2.5} className="relative z-10" />
              </div>
            )}
            <div className="absolute -bottom-1 -right-1 bg-emerald-500 rounded-lg p-1 border-2 border-[#1a2b3c] shadow-md">
              <Sparkles size={12} className="text-white" />
            </div>
          </div>
          <div className="min-w-0">
            <h3 className="text-xl font-black text-white truncate leading-tight">
              {profile?.first_name} {profile?.last_name}
            </h3>
            <div className="flex items-center gap-1.5 text-gray-400 mt-1">
              <Mail size={12} />
              <span className="text-[10px] font-bold truncate opacity-80 uppercase tracking-tight">{profile?.email}</span>
            </div>
          </div>
        </div>
        
        <div className="flex gap-2">
          <div className="bg-white/10 px-3 py-1.5 rounded-xl border border-white/5 flex items-center gap-2">
            <Globe size={12} className="text-emerald-400" />
            <span className="text-[9px] font-black uppercase tracking-widest">{profile?.currency || 'USD'}</span>
          </div>
          <div className="bg-white/10 px-3 py-1.5 rounded-xl border border-white/5 flex items-center gap-2">
            <Settings size={12} className="text-emerald-400" />
            <span className="text-[9px] font-black uppercase tracking-widest">{profile?.language || 'English'}</span>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-8 bg-white overflow-y-auto max-h-[500px] custom-scrollbar">
        {/* 1. SIDEKICK INTELLIGENCE */}
        <div className="space-y-4">
          <SectionHeader icon={BrainCircuit} title={t('settings.sidekick_config')} />
          <div className="bg-emerald-50 rounded-3xl p-5 border border-emerald-100/50 space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-[9px] font-black text-emerald-600/60 uppercase tracking-widest mb-1">{t('settings.identity')}</p>
                <p className="text-sm font-bold text-[#1a2b3c]">{aiProfile?.assistant?.name || t('settings.sidekick_default')}</p>
              </div>
              <span className="px-2 py-1 bg-emerald-500 text-white rounded-xl text-[8px] font-black uppercase tracking-widest shadow-sm">
                {aiProfile?.assistant?.persona ? t(`settings.personas.${aiProfile.assistant.persona}`) : t('settings.personas.efficiency')}
              </span>
            </div>
            <div>
              <p className="text-[9px] font-black text-emerald-600/60 uppercase tracking-widest mb-1">{t('settings.context_bio')}</p>
              <p className="text-[11px] font-medium text-gray-500 leading-relaxed italic">
                "{aiProfile?.global_bio || t('settings.no_context')}"
              </p>
            </div>
          </div>
        </div>

        {/* 2. DOMAIN: GROCERY */}
        {domains.grocery && (
          <div className="space-y-3">
            <SectionHeader icon={ShoppingCart} title={t('settings.grocery_intel')} />
            <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-2xl border border-gray-100">
              <DataPoint label={t('settings.fields.dietary')} value={domains.grocery.dietary} />
              <DataPoint label={t('settings.fields.allergies')} value={domains.grocery.allergies} />
              <DataPoint label={t('settings.fields.organic_pref')} value={domains.grocery.organic_pref ? t('common.yes') : t('common.no')} />
              <DataPoint label={t('settings.fields.pantry_staples')} value={domains.grocery.pantry_staples} />
            </div>
          </div>
        )}

        {/* 3. DOMAIN: APPAREL */}
        {domains.apparel && (
          <div className="space-y-3">
            <SectionHeader icon={Shirt} title={t('settings.apparel_profiling')} />
            <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100 space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <DataPoint label={t('settings.fields.shoe')} value={domains.apparel.sizes?.shoe} />
                <DataPoint label={t('settings.fields.top')} value={domains.apparel.sizes?.top} />
                <DataPoint label={t('settings.fields.waist')} value={domains.apparel.sizes?.waist ? `${domains.apparel.sizes.waist}"` : null} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <DataPoint label={t('settings.fields.color_palette')} value={domains.apparel.color_palette} />
                <DataPoint label={t('settings.fields.exclusions')} value={domains.apparel.exclusions} />
              </div>
            </div>
          </div>
        )}

        {/* 4. DOMAIN: ELECTRONICS */}
        {domains.electronics && (
          <div className="space-y-3">
            <SectionHeader icon={Cpu} title={t('settings.tech_ecosystem')} />
            <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-2xl border border-gray-100">
              <DataPoint label={t('settings.fields.ecosystems')} value={domains.electronics.ecosystems} />
              <DataPoint label={t('settings.fields.tech_level')} value={domains.electronics.tech_level} />
              <DataPoint label={t('settings.fields.warranty')} value={domains.electronics.warranty_priority ? t('settings.fields.priority') : t('settings.fields.standard')} />
            </div>
          </div>
        )}

            {/* Automotive domain details */}
            {domains.automotive && (
              <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
                <p className="text-[9px] font-black text-emerald-600 uppercase tracking-widest mb-2">{t('settings.auto_context')}</p>
                <div className="space-y-3">
                  {domains.automotive.vehicles?.map((v, i) => (
                    <div key={i} className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm space-y-2">
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 bg-gray-50 rounded-lg text-gray-400">
                            {v.type === 'bicycle' ? '🚲' : v.type === 'motorcycle' ? '🏍️' : v.type === 'boat' ? '🚤' : v.type === 'truck' ? '🚚' : v.type === 'rv' ? '🚐' : v.type === 'trailer' ? '🚛' : '🚗'}
                          </div>
                          <div>
                            <p className="text-[10px] font-bold text-[#1a2b3c]">{v.nickname ? `${v.nickname} • ` : ''}{v.year} {v.make} {v.model}</p>
                            <p className="text-[8px] font-black text-gray-400 uppercase tracking-tighter">{t(`settings.${v.type}`)}</p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          {v.fuel && (
                            <span className="text-[8px] font-black uppercase text-emerald-500 bg-emerald-50 px-2 py-0.5 rounded-lg border border-emerald-100">{t(`settings.${v.fuel}`)}</span>
                          )}
                          {v.gas_type && (
                            <span className="text-[7px] font-black uppercase text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-md border border-amber-100">{t(`settings.${v.gas_type}`)}</span>
                          )}
                        </div>
                      </div>
                      {v.insurance_provider && (
                        <div className="flex items-center gap-1.5 pt-1 border-t border-gray-50">
                          <span className="text-[7px] font-black text-gray-300 uppercase tracking-widest">{t('settings.fields.insurance')}</span>
                          <span className="text-[9px] font-bold text-gray-500">{v.insurance_provider}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="mt-3">
                  <DataPoint label={t('settings.fields.diy_maintenance')} value={domains.automotive.diy_level} />
                </div>
              </div>
            )}

        {/* 6. DOMAIN: HOME & LIVING */}
        {domains.home_living && (
          <div className="space-y-3">
            <SectionHeader icon={Home} title={t('settings.living_space')} />
            <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-2xl border border-gray-100">
              <DataPoint label={t('settings.fields.style')} value={domains.home_living.style} />
              <DataPoint label={t('settings.fields.rooms')} value={domains.home_living.rooms} />
              <DataPoint label={t('settings.fields.garden')} value={domains.home_living.garden ? t('common.yes') : t('common.no')} />
              <DataPoint label={t('settings.fields.tool_system')} value={domains.home_living.tool_system} />
            </div>
          </div>
        )}

        {/* 7. DOMAIN: DEPENDENTS */}
        {domains.dependents && (
          <div className="space-y-3">
            <SectionHeader icon={Heart} title={t('settings.household_dependents')} />
            <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100 space-y-4">
              <div className="flex flex-wrap gap-2">
                {domains.dependents.species?.map(s => (
                  <span key={s} className="text-[10px] font-bold text-[#1a2b3c] bg-white px-3 py-1.5 rounded-xl border border-gray-100 shadow-sm">🐾 {s}</span>
                ))}
                {domains.dependents.kids?.map((k, i) => {
                  const currentYear = new Date().getFullYear();
                  const age = k.birth_year ? currentYear - k.birth_year : null;
                  return (
                    <span key={i} className="text-[10px] font-bold text-[#1a2b3c] bg-white px-3 py-1.5 rounded-xl border border-gray-100 shadow-sm">
                      👶 {age !== null ? `${t('settings.fields.birth_year')} ${k.birth_year} (Age ${age})` : `${t('settings.fields.birth_year')} ${k.birth_year}`}
                    </span>
                  );
                })}
              </div>
              <DataPoint label={t('settings.fields.needs_context')} value={domains.dependents.specific_needs} />
            </div>
          </div>
        )}

        {/* 8. DOMAIN: HEALTH & FITNESS */}
        {domains.health && (
          <div className="space-y-3">
            <SectionHeader icon={Activity} title={t('settings.health_wellness')} />
            <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <DataPoint label={t('settings.fitness_policy')} value={domains.health.fitness_level} />
                <DataPoint label={t('settings.fields.skin_type')} value={domains.health.skin_type} />
              </div>
              <DataPoint label={t('settings.fields.medications')} value={domains.health.medications} />
              <DataPoint label={t('settings.fields.allergies')} value={domains.health.allergies} />
            </div>
          </div>
        )}

        {/* 9. DOMAIN: HOBBIES */}
        {domains.hobbies && (
          <div className="space-y-3">
            <SectionHeader icon={Palette} title={t('settings.hobbies_interests')} />
            <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100 space-y-4">
              <DataPoint label={t('settings.fields.interests')} value={domains.hobbies.interests} />
              <DataPoint label={t('settings.fields.learning_goals')} value={domains.hobbies.learning_goals} />
            </div>
          </div>
        )}
      </div>

      {/* Branded Footer */}
      <div className="px-6 py-4 bg-gray-50/50 flex justify-center border-t border-gray-100">
          <p className="text-[9px] font-black text-emerald-600/60 uppercase tracking-widest flex items-center gap-2">
            <Sparkles size={10} /> {t('settings.dna_active')}
          </p>
      </div>
    </div>
  );
};
